import { message } from 'antd';

import axiosInstance from '../config/axios';

/**
 * Bildirim tercihi için arayüz
 */
export interface NotificationPreference {
  id: number;
  userId: string;
  notificationType: string;
  isEnabled: boolean;
  channel: string;
}

/**
 * Bildirim alt türü için tercihler
 */
export interface NotificationActionPreference {
  actionType: string;
  displayName: string;
  isEnabled: boolean;
}

/**
 * Bildirim türü için tercihler
 */
export interface NotificationTypePreference {
  notificationType: string;
  displayName: string;
  actions: NotificationActionPreference[];
}

/**
 * Bildirim tercihleri için DTO
 */
export interface NotificationPreferenceDto {
  userId: string;
  preferences: NotificationTypePreference[];
}

/**
 * Push Notification için API yanıt tipi
 */
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Global değişken tanımlaması için TypeScript'e window nesnesini genişlet
declare global {
  interface Window {
    pushNotificationManager: any;
    PushNotificationManager: any; // PushNotificationManager sınıfı için
    isAuthenticated?: boolean;
    ENV?: {
      API_URL: string;
      APP_VERSION: string;
    };
    loadPushNotificationManager?: () => Promise<any>;
    PUSH_ALLOWED_ORIGINS?: string[];
    pushNotificationScriptLoaded?: boolean;
  }
}

/**
 * Bildirim servisi - Push notifications ve bildirim tercihleri için
 */
class NotificationService {
  public apiUrl: string;
  private pushManager: any;
  private initialized: boolean;
  private initializationPromise: Promise<void> | null;

  constructor() {
    // Öncelikle window.ENV.API_URL'yi kontrol et, yoksa process.env'den al
    this.apiUrl = window.ENV?.API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5180';
    this.pushManager = null;
    this.initialized = false;
    this.initializationPromise = null;
    this.initializePushManager();
  }
  
  /**
   * Push manager'ı başlat (public metod)
   */
  public async initializePushManager(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initializePushManager();
    return this.initializationPromise;
  }
  
  /**
   * Kullanıcının bildirim tercihlerini getirir
   */
  public async getNotificationPreferences(): Promise<NotificationTypePreference[]> {
    try {
      // Kullanıcı oturum açmış mı kontrol et
      if (!this.isAuthenticated()) {
        console.log('Bildirim tercihleri için oturum açılmamış');
        return [];
      }

      const response = await axiosInstance.get<ApiResponse<NotificationPreferenceDto>>(
        `/api/v1/notification-preferences/user`
      );
      
      if (response.data.success && response.data.data && Array.isArray(response.data.data.preferences)) {
        return response.data.data.preferences;
      } else {
        console.error('Bildirim tercihleri alınamadı:', response.data);
        return [];
      }
    } catch (error: any) {
      // 404 hatasını özel olarak ele al
      if (error.response?.status === 404) {
        console.warn('Bildirim tercihleri endpoint bulunamadı, varsayılan değerler kullanılıyor');
        return [];
      }
      console.error('Bildirim tercihleri alınırken hata:', error?.message || error);
      return [];
    }
  }
  
  /**
   * Kullanıcının bildirim tercihlerini günceller
   */
  public async updateNotificationPreferences(preferences: NotificationTypePreference[]): Promise<boolean> {
    try {
      if (!this.isAuthenticated()) {
        message.error('Bildirim tercihlerini güncellemek için oturum açmalısınız');
        return false;
      }
      
      // Backend'in beklediği NotificationPreferenceDto formatına dönüştür
      const notificationPreferenceDto = {
        userId: null, // Backend tarafında otomatik olarak ayarlanacak
        preferences: preferences
      };
      
      const response = await axiosInstance.post<ApiResponse<boolean>>(
        `/api/v1/notification-preferences/user`,
        notificationPreferenceDto
      );
      
      if (response.data.success) {
        message.success('Bildirim tercihleri başarıyla güncellendi');
        return true;
      } else {
        message.error(`Bildirim tercihleri güncellenemedi: ${response.data.message}`);
        return false;
      }
    } catch (error: any) {
      console.error('Bildirim tercihleri güncellenirken hata:', error?.message || error);
      message.error('Bildirim tercihleri güncellenirken bir hata oluştu');
      return false;
    }
  }

  /**
   * Push manager'ı başlat (private implementation)
   */
  private async _initializePushManager(): Promise<void> {
    try {
      console.log('🔧 Push manager başlatılıyor...');
      
      // Kullanıcı oturum açmamışsa stub manager oluştur
      if (!this.isAuthenticated()) {
        console.log('Kullanıcı oturum açmadığı için push notification manager başlatılmıyor.');
        this.createStubPushManager();
        this.initialized = true;
        return;
      }

      // Önce window.PushNotificationManager'ı kontrol et
      if (typeof window.PushNotificationManager === 'function') {
        try {
          console.log('✅ PushNotificationManager sınıfı bulundu, manager oluşturuluyor...');
          this.pushManager = new window.PushNotificationManager();
          await this.pushManager.initialize();
          console.log('✅ Push notification manager başarıyla oluşturuldu');
          this.initialized = true;
          return;
        } catch (error) {
          console.error('❌ PushNotificationManager oluşturulurken hata:', error);
        }
      }

      // index.html'de tanımlanan loadPushNotificationManager fonksiyonunu çağır
      if (typeof window.loadPushNotificationManager === 'function') {
        try {
          console.log('🔄 loadPushNotificationManager fonksiyonu çağrılıyor...');
          // loadPushNotificationManager bir Promise döndürüyor, onu bekleyelim
          const manager = await window.loadPushNotificationManager();
          if (manager) {
            this.pushManager = manager;
            console.log('✅ Push notification manager başarıyla alındı');
            this.initialized = true;
            return;
          }
        } catch (error) {
          console.error('❌ Push notification manager yüklenirken hata:', error);
        }
      }

      // Eğer yukarıdaki yöntem başarısız olduysa, fallback olarak window.pushNotificationManager'ı kontrol et
      if (window.pushNotificationManager) {
        this.pushManager = window.pushNotificationManager;
        console.log('✅ Push notification manager window objesinden alındı');
        this.initialized = true;
        return;
      }

      // Script'i manuel olarak yükle ve bekle
      await this.loadPushNotificationScript();
      
      // Script yüklendikten sonra tekrar dene
      if (typeof window.PushNotificationManager === 'function') {
        try {
          console.log('✅ Script yüklendikten sonra PushNotificationManager bulundu');
          this.pushManager = new window.PushNotificationManager();
          await this.pushManager.initialize();
          console.log('✅ Push notification manager başarıyla oluşturuldu (script yükleme sonrası)');
          this.initialized = true;
          return;
        } catch (error) {
          console.error('❌ Script yükleme sonrası PushNotificationManager oluşturulurken hata:', error);
        }
      }

      // Son çare olarak 3 saniye bekleyerek tekrar kontrol et
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      if (window.pushNotificationManager) {
        this.pushManager = window.pushNotificationManager;
        console.log('✅ Push notification manager başarıyla alındı (gecikme sonrası)');
        this.initialized = true;
      } else {
        console.warn('⚠️ Push notification manager yüklenemedi, stub manager oluşturuluyor');
        // Stub manager oluştur - hata almamak için
        this.createStubPushManager();
        this.initialized = true;
      }
    } catch (error: any) {
      console.error('❌ Push manager başlatılırken hata:', error?.message || error);
      // Stub manager oluştur - hata almamak için
      this.createStubPushManager();
      this.initialized = true;
    }
  }

  /**
   * Push notification script'ini manuel olarak yükler
   */
  private async loadPushNotificationScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Script zaten yüklenmişse direkt resolve
      if (window.pushNotificationScriptLoaded) {
        resolve();
        return;
      }

      console.log('Push notification script manuel olarak yükleniyor...');
      const script = document.createElement('script');
      script.src = '/js/push-notification.js';
      script.async = true;
      
      script.onload = () => {
        console.log('Push notification script başarıyla yüklendi');
        window.pushNotificationScriptLoaded = true;
        // Script yüklendikten sonra kısa bir bekleme
        setTimeout(resolve, 100);
      };
      
      script.onerror = (error) => {
        console.error('Push notification script yüklenemedi:', error);
        reject(new Error('Push notification script yüklenemedi'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Kullanıcının oturum açıp açmadığını kontrol eder
   */
  private isAuthenticated(): boolean {
    try {
      // localStorage ve sessionStorage'dan token kontrolü
      const hasTokenInLocalStorage = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const hasTokenInSessionStorage = sessionStorage.getItem('accessToken') || sessionStorage.getItem('token');
      const hasToken = Boolean(hasTokenInLocalStorage || hasTokenInSessionStorage);
      const isAuthenticatedFlag = window.isAuthenticated === true;
      return hasToken || isAuthenticatedFlag;
    } catch (error) {
      console.error('Oturum kontrolü sırasında hata:', error);
      return false;
    }
  }

  /**
   * Stub push manager oluşturur (gerçek manager yüklenemediğinde)
   */
  private createStubPushManager(): void {
    this.pushManager = {
      // Stub metodlar - hata fırlatmaz, sadece false döner
      subscribe: async () => {
        console.warn('Stub push manager: subscribe çağrıldı');
        return false;
      },
      unsubscribe: async () => {
        console.warn('Stub push manager: unsubscribe çağrıldı');
        return false;
      },
      checkSubscription: async () => {
        console.warn('Stub push manager: checkSubscription çağrıldı');
        return false;
      },
      sendTestNotification: async () => {
        console.warn('Stub push manager: sendTestNotification çağrıldı');
        return false;
      },
      initialize: async () => {
        console.warn('Stub push manager: initialize çağrıldı');
        return false;
      }
    };
  }

  /**
   * Push manager'ın hazır olmasını bekler
   */
  private async ensurePushManagerReady(): Promise<boolean> {
    if (!this.initialized) {
      await this.initializePushManager();
    }

    if (!this.pushManager) {
      console.warn('Push manager hazır değil');
      return false;
    }

    return true;
  }

  /**
   * Push bildirimlere abone ol
   */
  public async subscribeToPushNotifications(): Promise<boolean> {
    try {
      if (!this.isAuthenticated()) {
        message.error('Push bildirimlere abone olmak için oturum açmalısınız');
        return false;
      }

      const isReady = await this.ensurePushManagerReady();
      if (!isReady) {
        message.warning('Push notification sistemi hazır değil');
        return false;
      }

      const result = await this.pushManager.subscribe();
      if (result) {
        message.success('Push bildirimlere başarıyla abone oldunuz');
      } else {
        message.warning('Push bildirimlere abone olunamadı');
      }
      return result;
    } catch (error: any) {
      console.error('Push bildirimlere abone olunurken hata:', error?.message || error);
      message.error('Push bildirimlere abone olunurken bir hata oluştu');
      return false;
    }
  }

  /**
   * Push bildirim aboneliğini iptal et
   */
  public async unsubscribeFromPushNotifications(): Promise<boolean> {
    try {
      if (!this.isAuthenticated()) {
        message.error('Push bildirim aboneliğini iptal etmek için oturum açmalısınız');
        return false;
      }

      const isReady = await this.ensurePushManagerReady();
      if (!isReady) {
        message.warning('Push notification sistemi hazır değil');
        return false;
      }

      const result = await this.pushManager.unsubscribe();
      if (result) {
        message.success('Push bildirim aboneliği başarıyla iptal edildi');
      } else {
        message.warning('Push bildirim aboneliği iptal edilemedi');
      }
      return result;
    } catch (error: any) {
      console.error('Push bildirim aboneliği iptal edilirken hata:', error?.message || error);
      message.error('Push bildirim aboneliği iptal edilirken bir hata oluştu');
      return false;
    }
  }

  /**
   * Push bildirim abonelik durumunu kontrol et
   */
  public async checkPushNotificationSubscription(): Promise<boolean> {
    try {
      if (!this.isAuthenticated()) {
        console.log('Kullanıcı oturum açmadığı için abonelik kontrolü yapılamadı');
        return false;
      }

      const isReady = await this.ensurePushManagerReady();
      if (!isReady) {
        console.warn('Push notification sistemi hazır değil');
        return false;
      }

      return await this.pushManager.checkSubscription();
    } catch (error: any) {
      console.error('Push bildirim abonelik kontrolü sırasında hata:', error?.message || error);
      return false;
    }
  }
  
  /**
   * Eski metot adı - geriye dönük uyumluluk için
   */
  public async checkSubscriptionStatus(): Promise<boolean> {
    return this.checkPushNotificationSubscription();
  }

  /**
   * Test bildirimi gönder
   */
  public async sendTestPushNotification(): Promise<boolean> {
    try {
      if (!this.isAuthenticated()) {
        message.error('Test bildirimi göndermek için oturum açmalısınız');
        return false;
      }

      // Abonelik durumunu kontrol et
      const isSubscribed = await this.checkPushNotificationSubscription();
      if (!isSubscribed) {
        message.warning('Test bildirimi göndermek için önce bildirimlere abone olmalısınız');
        return false;
      }

      // Test bildirimi gönder
      const response = await axiosInstance.post<ApiResponse<boolean>>(
        `/api/v1/notification/send-test`
      );
      
      if (response.data.success) {
        message.success('Test bildirimi başarıyla gönderildi');
        return true;
      } else {
        message.error(`Test bildirimi gönderilemedi: ${response.data.message}`);
        return false;
      }
    } catch (error: any) {
      console.error('Test bildirimi gönderirken hata:', error?.message || error);
      message.error('Test bildirimi gönderirken bir hata oluştu');
      return false;
    }
  }
  
  /**
   * Eski metot adı - geriye dönük uyumluluk için
   */
  public async sendTestNotification(): Promise<ApiResponse<boolean>> {
    try {
      const result = await this.sendTestPushNotification();
      return {
        success: result,
        message: result ? 'Test bildirimi başarıyla gönderildi' : 'Test bildirimi gönderilemedi',
        data: result
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Test bildirimi gönderirken bir hata oluştu',
        data: false
      };
    }
  }
};

// Singleton instance
const notificationServiceInstance = new NotificationService();

// Push bildirimlere abone olma işlevi (NotificationSubscriber bileşeni için)
export const subscribeToPushNotifications = async (): Promise<boolean> => {
  return await notificationServiceInstance.subscribeToPushNotifications();
};

// Push bildirim aboneliğini iptal etme işlevi
export const unsubscribeFromPushNotifications = async (): Promise<boolean> => {
  return await notificationServiceInstance.unsubscribeFromPushNotifications();
};

// Push bildirim abonelik durumunu kontrol etme işlevi
export const checkSubscriptionStatus = async (): Promise<boolean> => {
  return await notificationServiceInstance.checkPushNotificationSubscription();
};

// Test bildirimi gönderme işlevi
export const sendTestNotification = async (): Promise<ApiResponse<boolean>> => {
  return await notificationServiceInstance.sendTestNotification();
};

// Bildirim tercihlerini getirme işlevi
export const getNotificationPreferences = async (): Promise<NotificationTypePreference[]> => {
  return await notificationServiceInstance.getNotificationPreferences();
};

// Bildirim tercihlerini güncelleme işlevi
export const updateNotificationPreferences = async (preferences: NotificationTypePreference[]): Promise<boolean> => {
  return await notificationServiceInstance.updateNotificationPreferences(preferences);
};

export default notificationServiceInstance;
